import { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Download, Filter, RefreshCw, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { SupplierRatingThreshold } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
// CR-4783 follow-up: share the dashboard's Quick Date Ranges helper so the
// two screens compute the same dateFrom/dateTo for a given preset.
import { getLast12MonthsRange } from '../dashboard/DashboardFilters';
import FilterSections from './performance-filters/FilterSections';
import useFilterOptions from './performance-filters/useFilterOptions';
import useSupplierSiteOptions from './performance-filters/useSupplierSiteOptions';
import useHierarchyScope from './performance-filters/useHierarchyScope';

export interface SupplierPerformanceFilterValues {
  division: string;
  site: string;
  supplierGroup: string;
  supplierSite: string;
  supplierCode: string;
  warehouse: string;
  strategic: boolean;
  commodityGroup: string;
  commodityType: string;
  timePeriod: string;
  dateFrom: string; // YYYY-MM-DD format
  dateTo: string; // YYYY-MM-DD format
  supplierType: string;
  rating: string;
  excludeNARatings: boolean;
  dataMode: 'historical' | 'live';
  // CR-4783: Geographic filters
  siteCountry: string;
  supplierCountry: string;
}

// CR-7264 rework: the ONE default filter set for the page. Clear all used to
// reset to a second copy with no date range, so "clearing" silently switched
// the page from the 12-month view it opened on to all-time figures — and a
// fresh page reported the default range as "3 active" filters.
const DEFAULT_RANGE = getLast12MonthsRange();

export const DEFAULT_FILTER_VALUES: SupplierPerformanceFilterValues = {
  division: 'all',
  site: 'all',
  supplierGroup: 'all',
  supplierSite: 'all',
  supplierCode: '',
  warehouse: 'all',
  strategic: false,
  commodityGroup: 'all',
  commodityType: 'all',
  timePeriod: 'last12Months',
  dateFrom: DEFAULT_RANGE.dateFrom,
  dateTo: DEFAULT_RANGE.dateTo,
  supplierType: 'all',
  rating: 'all',
  excludeNARatings: true,
  dataMode: 'historical',
  siteCountry: 'all',
  supplierCountry: 'all',
};

interface SupplierPerformanceFiltersProps {
  filters: SupplierPerformanceFilterValues;
  onApplyFilters: (filters: SupplierPerformanceFilterValues) => void;
  ratingThresholds: SupplierRatingThreshold[];
  /**
   * CR-6403: the design puts Refresh and Export in the filter header alongside
   * Hide filters. Both are page-level actions, so they are passed in; the
   * header still renders without them.
   */
  onRefresh?: () => void;
  onExport?: () => void;
  /** CR-7297: the page's one Export control — busy, unavailable, and what it writes. */
  exporting?: boolean;
  exportDisabled?: boolean;
  exportTitle?: string;
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type QuickRangeKey = 'last12Months' | 'thisMonth' | 'lastMonth' | 'lastQuarter' | 'thisYear' | 'lastYear';

const QUICK_RANGE_ORDER: QuickRangeKey[] = [
  'last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear',
];

/**
 * One label per preset, used by both the button row and the footer's
 * "Showing results for …" line, so the two can never disagree — the same
 * pairing the dashboard keeps in `QUICK_RANGE_LABELS`.
 */
const QUICK_RANGE_LABELS: Record<QuickRangeKey, string> = {
  last12Months: 'Last 12 Months (Default)',
  thisMonth: 'This Month (Live)',
  lastMonth: 'Last Month',
  lastQuarter: 'Last Quarter',
  thisYear: 'This Year',
  lastYear: 'Last Year',
};

function datesForQuickRange(range: QuickRangeKey): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'last12Months':
      return getLast12MonthsRange();
    case 'thisMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
  }
}

// CR-7273: the Order Type popover was removed. Four of its five values did not
// exist on PurchaseOrder.order_type, the fifth is never written by any import,
// and the selection never left the browser — it only made the panel report a
// filter as active. The column itself is dormant; see the model field.

// CR-4783: Supplier Type now segments by organisational relationship
// (same group vs different group relative to the viewing customer).
const SUPPLIER_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

export default function SupplierPerformanceFilters({
  filters,
  onApplyFilters,
  ratingThresholds,
  onRefresh,
  onExport,
  exporting = false,
  exportDisabled = false,
  exportTitle,
}: SupplierPerformanceFiltersProps) {
  const { user } = useAuth();
  // CR-4783 round 2: divisions are *customer-side CompanyGroups* (e.g.
  // "Acme – Aerospace Systems"), not customer companies / sites. The round 1
  // fix sourced this list from getAccessibleCompanies which returns sites
  // (e.g. "Acme – Aerospace – UK | Daresbury") so the dropdown read as a
  // site picker rather than a division picker, exactly as Sarah flagged.
  const {
    divisions, sites, supplierSites, warehouses, supplierGroups, commodityGroups, commodityTypes,
  } = useFilterOptions(user?.platform);
  const [expanded, setExpanded] = useState(false); // Start collapsed for cleaner UX

  // Local pending filter state — only synced to parent when Apply or Clear is clicked
  const [localFilters, setLocalFilters] = useState<SupplierPerformanceFilterValues>(filters);

  // Sync local state if parent resets filters externally (e.g. from Clear All)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);


  const updateFilter = <K extends keyof SupplierPerformanceFilterValues>(
    key: K,
    value: SupplierPerformanceFilterValues[K]
  ) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  // Which Divisions / Company Sites this user may pick, and whether the
  // top-bar view context locks either. See useHierarchyScope.
  const {
    scopedDivisions, isDivisionLocked, isSiteLocked, viewFilteredSites, handleDivisionChange,
  } = useHierarchyScope(divisions, sites, user?.platform, localFilters, setLocalFilters, onApplyFilters);

  const supplierSiteOptions = useSupplierSiteOptions(supplierSites, localFilters, setLocalFilters);

  // CR-4783 round 3: Quick Date Range now auto-applies. Sarah's feedback
  // was that clicking a preset changed the dateFrom/dateTo inputs but didn't
  // touch the data underneath until Apply Filters was pressed — which felt
  // broken. The dashboard preset row applies on click; mirror that here.
  const applyQuickRange = (range: QuickRangeKey) => {
    const { dateFrom, dateTo } = datesForQuickRange(range);
    const next = { ...localFilters, dateFrom, dateTo, timePeriod: range };
    setLocalFilters(next);
    onApplyFilters(next);
  };

  // CR-4783 round 3: country dropdowns auto-apply too — same usability
  // complaint as Quick Date Ranges.
  const applyAndUpdate = <K extends keyof SupplierPerformanceFilterValues>(
    key: K,
    value: SupplierPerformanceFilterValues[K],
  ) => {
    const next = { ...localFilters, [key]: value };
    setLocalFilters(next);
    onApplyFilters(next);
  };

  const activeQuickRange: QuickRangeKey | null = QUICK_RANGE_ORDER.find((range) => {
    const d = datesForQuickRange(range);
    return d.dateFrom === localFilters.dateFrom && d.dateTo === localFilters.dateTo;
  }) ?? null;

  const clearFilters = () => {
    setLocalFilters(DEFAULT_FILTER_VALUES);
    onApplyFilters(DEFAULT_FILTER_VALUES);
  };

  /**
   * A selection has been made that the table has not been re-queried for.
   *
   * Most controls here are deliberately deferred — they change `localFilters`
   * and wait for Apply — so without this the panel gives no sign that the
   * numbers on screen no longer answer the question on screen. (Quick ranges,
   * country pickers and the division clear apply on click, which pushes new
   * `filters` down and settles this back to false by itself.)
   */
  const hasPendingChanges = useMemo(
    () => (Object.keys(localFilters) as Array<keyof SupplierPerformanceFilterValues>)
      .some((key) => localFilters[key] !== filters[key]),
    [localFilters, filters],
  );

  const rangeSummary = activeQuickRange
    ? QUICK_RANGE_LABELS[activeQuickRange]
    : (filters.dateFrom && filters.dateTo
      ? `${filters.dateFrom} to ${filters.dateTo}`
      : 'all dates');

  // Counted from the APPLIED filters, not the pending ones. Counting the
  // pending selection put two contradictory statements side by side in the
  // bar — "1 filter active" next to the unapplied-changes pill, above a table
  // that had no filter applied at all. The count describes the results on
  // screen; the pill describes the edit that has not reached them.
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'excludeNARatings') return !value; // Count if unchecked (default is true)
    if (key === 'strategic') return value === true;
    // CR-7273 / CR-7264: the Order Type and Hide Child Companies controls are
    // gone (the latter never filtered anything), but state persisted before
    // the change can still carry the keys. A filter the user can neither see
    // nor clear must never count as active, whatever value it holds.
    if (key === 'orderType' || key === 'hideChildCompanies') return false;
    // Same rule for a scope the view context has locked: the picker is greyed
    // out and Clear all cannot move it, so it is the page's scope rather than
    // a filter the user chose. Counting it made a completely unfiltered page
    // open reporting "1 filter active".
    if (key === 'site' && isSiteLocked) return false;
    if (key === 'division' && isDivisionLocked) return false;
    // Any list-valued filter counts only when something is picked (`[] !== 'all'`
    // would otherwise report an empty list as active).
    if (Array.isArray(value)) return value.length > 0;
    // The date range is a filter only when it differs from the default one;
    // timePeriod merely names the range the dates already express.
    if (key === 'dateFrom' || key === 'dateTo') return value !== DEFAULT_FILTER_VALUES[key];
    if (key === 'timePeriod') return false;
    if (key === 'dataMode') return value !== 'historical';
    return value !== 'all' && value !== '';
  }).length;

  return (
    /* CR-6403 rework 2: the same card every other filter surface uses —
       Dashboard `DashboardFilterBar`, PO Lines `CustomerSectionedFilters`,
       Quality `NCRFiltersBar`: a "Filter …" title on the left; Refresh and
       Export as small outline buttons with icons and a "Filters" toggle
       carrying the active count as a badge on the right; the sections open
       inside the same card over a Clear All / Apply Filters footer. The
       CR-7264 "Changes not applied" prompt is kept: it is what tells a user
       that a deferred selection has not reached the table yet. */
    <Card className="mb-4">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-xl">Filter Supplier Performance</CardTitle>
            {/* Visible whether the panel is open or shut — a selection made and
                then collapsed away must not look applied. Clicking it opens the
                panel, which is where Apply lives. */}
            {hasPendingChanges && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 transition-colors"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Changes not applied
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {onRefresh && (
              <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            )}
            {/* CR-7297: the page's only Export — it writes the view on screen. */}
            {onExport && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onExport}
                disabled={exporting || exportDisabled}
                aria-busy={exporting}
                title={exportTitle}
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
            )}
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                expanded
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span
                  data-testid="active-filter-count"
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    expanded
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-primary text-primary-foreground'
                  }`}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-0">
          {/* CR-6403 rework 3: no `border-t` here. The rule under the header is
              now the first inset SectionDivider inside FilterSections, matching
              ASNFilterSections and the Deliveries layouts; a full-bleed line
              here would sit above it and read as a double divider. */}
          <div className="bg-card">
            <FilterSections
              localFilters={localFilters}
              setLocalFilters={setLocalFilters}
              updateFilter={updateFilter}
              applyAndUpdate={applyAndUpdate}
              scopedDivisions={scopedDivisions}
              isDivisionLocked={isDivisionLocked}
              onDivisionChange={handleDivisionChange}
              viewFilteredSites={viewFilteredSites}
              isSiteLocked={isSiteLocked}
              supplierGroups={supplierGroups}
              supplierSites={supplierSites}
              supplierSiteOptions={supplierSiteOptions}
              warehouses={warehouses}
              commodityGroups={commodityGroups}
              commodityTypes={commodityTypes}
              ratingThresholds={ratingThresholds}
              supplierTypes={SUPPLIER_TYPES}
              // CR-4783: Quick Date Ranges — the same button row as the
              // Dashboard, so users meet one control across screens.
              // Selecting a preset populates dateFrom/dateTo and applies.
              quickRanges={QUICK_RANGE_ORDER.map((range) => (
                <button
                  key={range}
                  type="button"
                  onClick={() => applyQuickRange(range)}
                  className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                    activeQuickRange === range
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                  }`}
                >
                  {QUICK_RANGE_LABELS[range]}
                </button>
              ))}
            />

            {/* Filter footer — Clear All (left), what the results cover
                (centre), Apply Filters (right): the dashboard's footer, with
                the same shared Button sizes, icons and labels as PO Lines. */}
            <div className="px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Button type="button" variant="outline" onClick={clearFilters} className="px-6">
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>

                {hasPendingChanges ? (
                  <p className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertCircle className="h-4 w-4" />
                    Your selection isn't in the results yet — click Apply Filters
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Showing results for {rangeSummary}</p>
                )}

                <Button
                  type="button"
                  onClick={() => onApplyFilters(localFilters)}
                  className={`px-6 ${
                    hasPendingChanges ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-background' : ''
                  }`}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
